/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Sector overlay family (Task #64,
 *  operator-cop-doctrine §3A.7 / §8.4 colour-context discipline; §4.3 the
 *  sector AMBIENT register — thin dashed line, no competing fill).
 *
 *  This module is the SINGLE source of the sector overlay palette. It is
 *  consumed ONLY by the sector family:
 *    - SectorsLayer map polygons (the dashed boundary stroke)
 *    - SectorDrawTool live-draw preview stroke
 *    - the SectorBody side-panel open/closed badge
 *
 *  Palette (locked law — do NOT re-tune the hex values here):
 *    open    amber  #a98f3d   ← active sector; Colour Law 4 ambient desaturate
 *                               (from #d4a017), well below the vivid L3 amber
 *    closed  grey   #6b7280   ← soft-closed sector (demobilized)
 *
 *  Sectors live in the AMBIENT register (§4.3): the boundary is a thin DASHED
 *  line that never pulses (§3B.8 motion budget) and never carries a saturated
 *  fill that would compete with hazard polygons beneath it. A very low-opacity
 *  fill is allowed only as a faint click/hover affordance.
 *
 *  Overlap debt this lock acknowledges (§3A.7): amber ALSO means IMS Response
 *  Level 3 (styles/responseLevel) and OC-Monitored fire — a deliberately MUTED
 *  amber (#a98f3d, Colour Law 4 ambient register, not the vivid #f59e0b) keeps the
 *  sector boundary ambient and distinct. Colour is never load-bearing alone: every
 *  render pairs the stroke with the sector name + an "Open"/"Closed" text label.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards /
 *     ./communityStatus / ./rawHotspots / ./lifecycleStates (enforced by the
 *     no-restricted-imports lint rule in eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

/** The two sector states' hexes — the single source of truth. */
export const SECTOR_OPEN_COLOR = '#a98f3d'; // ambient muted amber (Law 4, active)
export const SECTOR_CLOSED_COLOR = '#6b7280'; // grey (soft-closed)

/** Faint fill alpha — a barely-there click/hover affordance, never a hazard fill. */
export const SECTOR_FILL_ALPHA = 0.06;
/** Dashed-stroke alpha for the boundary line. */
export const SECTOR_STROKE_ALPHA = 0.9;
/** Cesium polyline dash pattern (16-bit) — a thin dashed line per §4.3. */
export const SECTOR_DASH_PATTERN = 0b1110011110001100;

export interface SectorStyle {
  /** 'open' (closed_at null) or 'closed'. */
  state: 'open' | 'closed';
  /** CSS/Cesium hex for the dashed stroke + badge. */
  color: string;
  /** Short label — colour is never load-bearing alone. */
  label: string;
}

const OPEN_STYLE: SectorStyle = { state: 'open', color: SECTOR_OPEN_COLOR, label: 'Open' };
const CLOSED_STYLE: SectorStyle = {
  state: 'closed',
  color: SECTOR_CLOSED_COLOR,
  label: 'Closed',
};

/**
 * Resolve the render style for a sector from its `closed_at` field. Tolerant of
 * null/undefined (→ open). A sector with any closed_at is rendered grey.
 */
export function sectorStyle(closedAt: string | null | undefined): SectorStyle {
  return closedAt ? CLOSED_STYLE : OPEN_STYLE;
}
